import { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, X, ArrowRight, CheckCircle2, Zap } from "lucide-react";
import { CATEGORIES, REGIONS, VOLUMES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const field = "w-full bg-white border border-slate-200 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 outline-none px-4 py-3 text-[#0A192F] placeholder:text-slate-400 transition-all duration-300";
const label = "font-head text-[11px] tracked uppercase text-[#0A192F]/60 mb-2 block";

export default function QuoteSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", region: "", product_category: "", volume: "", message: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addFiles = (list) => setFiles((prev) => [...prev, ...Array.from(list)].slice(0, 8));
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and requirement details.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("company", form.company);
      fd.append("phone", form.phone);
      fd.append("product_category", form.product_category);
      fd.append("industry", form.region);
      fd.append("quantity", form.volume);
      fd.append("message", form.message);
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/rfq`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Request routed to our team — we'll respond shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally { setLoading(false); }
  };

  return (
    <section id="quote" className="bg-[#F8FAFC] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left rail */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 gold-gradient rounded-full px-4 py-1.5 mb-6">
              <Zap size={14} color="#0A192F" />
              <span className="font-head text-[11px] tracked uppercase text-[#0A192F] font-bold">Instant Quote Routing</span>
            </div>
            <h2 className="font-head font-extrabold tracked uppercase text-3xl lg:text-4xl text-[#0A192F] leading-tight">
              Quote & Speed Calculator
            </h2>
            <p className="text-slate-500 mt-5 leading-relaxed max-w-md">
              Tell us your component type and volume. Attach drawings or specs and our sourcing team routes an accurate quote directly to you.
            </p>
            <div className="mt-8 space-y-4">
              {["Global sourcing & optimised logistics", "Confidential — reviewed by our engineers", "Response within 2–3 business days"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-[#0A192F]/70 text-sm">
                  <CheckCircle2 size={18} className="text-[#D4AF37] shrink-0" /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* Form card */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl shadow-card-lg border border-slate-100 p-7 lg:p-10">
              {done ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}
                  className="flex flex-col items-start justify-center min-h-[380px]" data-testid="quote-success">
                  <div className="w-14 h-14 rounded-full gold-gradient flex items-center justify-center mb-6">
                    <CheckCircle2 size={30} color="#0A192F" />
                  </div>
                  <h3 className="font-head font-extrabold tracked uppercase text-2xl text-[#0A192F]">Request Received</h3>
                  <p className="text-slate-500 mt-4 max-w-md">Thank you, {form.name.split(" ")[0]}. Our sourcing team is reviewing your requirements{files.length ? ` and ${files.length} attached file${files.length > 1 ? "s" : ""}` : ""} and will respond within 2–3 business days.</p>
                  <button onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", phone: "", region: "", product_category: "", volume: "", message: "" }); setFiles([]); }}
                    data-testid="quote-new-btn" className="mt-8 font-head font-bold text-sm tracked-sm uppercase gold-text">Submit another request →</button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <span className={label}>Component Type</span>
                      <select data-testid="quote-component" className={field} value={form.product_category} onChange={set("product_category")}>
                        <option value="">Select</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <span className={label}>Volume</span>
                      <select data-testid="quote-volume" className={field} value={form.volume} onChange={set("volume")}>
                        <option value="">Select</option>
                        {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><span className={label}>Full Name *</span><input data-testid="quote-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" /></div>
                    <div><span className={label}>Email *</span><input data-testid="quote-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="jane@company.com" /></div>
                    <div><span className={label}>Company</span><input data-testid="quote-company" className={field} value={form.company} onChange={set("company")} placeholder="Company Inc." /></div>
                    <div><span className={label}>Phone</span><input data-testid="quote-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="+1 ..." /></div>
                  </div>

                  <div>
                    <span className={label}>Requirements *</span>
                    <textarea data-testid="quote-message" className={field + " min-h-[110px] resize-y"} value={form.message} onChange={set("message")} placeholder="Describe specifications, materials, tolerances and application." />
                  </div>

                  <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                    onClick={() => inputRef.current?.click()} data-testid="quote-dropzone"
                    className="border-2 border-dashed border-slate-200 hover:border-[#D4AF37] rounded-lg cursor-pointer p-7 text-center transition-colors duration-300">
                    <Upload size={22} className="mx-auto text-[#D4AF37] mb-3" />
                    <p className="text-[#0A192F]/70 text-sm">Drop drawings & specs, or <span className="gold-text font-semibold">browse</span></p>
                    <p className="text-slate-400 text-xs mt-1">PDF, DWG, DXF, STEP, images · up to 8 files</p>
                    <input ref={inputRef} type="file" multiple hidden data-testid="quote-file-input" onChange={(e) => addFiles(e.target.files)} />
                  </div>

                  {files.length > 0 && (
                    <div className="space-y-2" data-testid="quote-file-list">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#F8FAFC] border border-slate-200 rounded-lg px-4 py-2.5 text-sm">
                          <span className="flex items-center gap-2 text-[#0A192F]/80 truncate"><FileText size={15} className="text-[#D4AF37] shrink-0" /><span className="truncate">{f.name}</span></span>
                          <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-[#0A192F] shrink-0"><X size={16} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="quote-submit-btn"
                    className="btn-gold group w-full inline-flex items-center justify-center gap-3 font-head font-bold text-sm tracked-sm uppercase px-8 py-4 rounded-lg disabled:opacity-60">
                    {loading ? "Routing your request…" : "Get Precision Quote"}
                    {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
